import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { LeadEntityType, NoticesStagesv2 } from "lib/types/graphQLEnums";
import { BidDetails } from "../../lib/hooks/api/bid/bids/useBid";
import { LeadDetails } from "../../lib/hooks/api/leads/useLead";
import { routes } from "../../lib/routes";
import { BidEntityType } from "../../lib/types/graphQLEnums";
import { Flex } from "../../styles/utility-components";
import CreateBidButton from "../bids/create_bid_button/CreateBidButton";
import CreateOrViewLeadButton from "../leads/saving/CreateOrViewLeadButton";
import { HideNoticeButton } from "../record_qualifications/HideNoticeButton";
import Button from "../ui/button/Button";
import { CreateBidNoBidReportButton } from "./CreateBidNoBidReportButton";
import { RestoreNoticeButton } from "./RestoreNoticeButton";

type Props = {
  size?: "small" | "default";
  contextSource: string;
  hideSecondaryActions?: boolean;
  // When true, replaces the Create/View bid button with CreateBidNoBidReportButton
  canCreateBidReport?: boolean;
  notice: {
    id: string;
    name: string;
    value: number | undefined;
    url?: string;
    procurementStage: {
      id: string;
      stage: string;
    };
    isHidden: boolean;
    stage: string;
    stageV2?: NoticesStagesv2 | undefined;
    closeDate: string | undefined;
    expiryDate: string | undefined;
    buyers: string[];
    hasBuyerLeads?: boolean;
    suppliers: string[];
    score: number | null | undefined;
    signalTypes: Record<string, string[]> | undefined;
    leads?: Pick<LeadDetails, "id" | "name">[] | undefined;
    bid?: Pick<BidDetails, "id"> | undefined;
  };
};
export function NoticeActions({
  notice,
  size = "default",
  contextSource,
  hideSecondaryActions,
  canCreateBidReport = false,
}: Props) {
  const currentUser = useAssertCurrentUser();

  const isOpenTender = notice.stageV2 === NoticesStagesv2.OpenTender;

  if (notice.isHidden) {
    return <RestoreNoticeButton notice={notice} contextSource={contextSource} size={size} />;
  }

  const bidDefaultValues = {
    name: notice.name,
    value: notice.value,
    submissionDate: notice.closeDate,
    entities: [
      { entityId: notice.procurementStage.id, entityType: BidEntityType.Notice },
      ...notice.buyers.map((buyerId) => ({
        entityId: buyerId,
        entityType: BidEntityType.Buyer,
      })),
      ...notice.suppliers.map((id) => ({
        entityId: id,
        entityType: BidEntityType.Supplier,
      })),
    ],
  };

  const viewBidButton = notice.bid ? (
    <Button
      fullWidth={hideSecondaryActions}
      buttonType="secondary"
      as="a"
      onClickCapture={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      // eslint-disable-next-line no-restricted-syntax -- left-click navigation intentionally prevented; href is for middle-click/accessibility
      href={`${routes.bids}/${notice.bid.id}`}
    >
      View bid
    </Button>
  ) : null;

  const createBidButton = (buttonType: "primary" | "secondary") => (
    <CreateBidButton
      trackingData={{ "Context source": contextSource }}
      createdFrom="notice"
      defaultValues={bidDefaultValues}
      buttonProps={{ buttonType, fullWidth: hideSecondaryActions }}
    />
  );

  return (
    <Flex gap={8}>
      {isOpenTender ? (
        canCreateBidReport ? (
          // CreateBidNoBidReportButton first (it manages primary/secondary itself), then View bid or
          // Create bid, always secondary: stacked in a full-width card, side by side otherwise.
          <Flex
            flexDirection={hideSecondaryActions ? "column" : "row"}
            gap={8}
            style={hideSecondaryActions ? { flex: 1 } : undefined}
          >
            <CreateBidNoBidReportButton
              trackingData={{ "Context source": contextSource }}
              defaultValues={bidDefaultValues}
              sourceUrl={notice.url}
              existingBidId={notice.bid?.id}
              fullWidth={hideSecondaryActions}
            />
            {viewBidButton ?? createBidButton("secondary")}
          </Flex>
        ) : (
          viewBidButton ?? createBidButton("primary")
        )
      ) : (
        <CreateOrViewLeadButton
          trackingData={{
            "Context source": contextSource,
          }}
          savedLeadIds={notice.leads?.map((lead) => lead.id) || []}
          entityId={notice.procurementStage.id}
          entityType="notice"
          hasBuyerLeads={notice.hasBuyerLeads ?? false}
          buttonProps={{
            fullWidth: hideSecondaryActions,
          }}
          defaultValues={{
            name: notice.name,
            value: notice.value,
            assignedToId: currentUser.guid,
            description: `Lead created from [${notice.name || "this notice"}](/notices/${notice.id})`,
            entities: [
              {
                entityId: notice.procurementStage.id,
                entityType: LeadEntityType.Notice,
              },
              ...notice.buyers.map((buyer) => ({
                entityId: buyer,
                entityType: LeadEntityType.Buyer,
              })),
              ...notice.suppliers.map((id) => ({
                entityId: id,
                entityType: LeadEntityType.Supplier,
              })),
            ],

            expiryDate: notice.expiryDate,
            noticeStage: notice.procurementStage.stage,
            stage: notice.stage,
          }}
        />
      )}

      {!hideSecondaryActions && (
        <HideNoticeButton
          noticeName={notice.name}
          procurementStage={notice.procurementStage}
          score={notice.score}
          signalTypes={notice.signalTypes}
          contextSource={contextSource}
          noticeId={notice.id}
          size={size}
        />
      )}
    </Flex>
  );
}
